import { useCallback } from "react";
import type { ChartConfig, TickProps } from "@ptsd/uikit";
import {
  Card,
  ChartContainer,
  ChartTooltip,
  Chips,
  Collapsible,
  Gear,
  getChartLines,
  Legend,
  YAxisTick,
} from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Circle, Text, View, XStack, YStack } from "tamagui";

import type { RouterOutputs } from "~/utils/api";
import { dateToDDMM, dateToReadable } from "~/utils/date-to-readable";
import { LinearChartFilter } from "~/component/blocks/linear-chart-filter";
import { useGetChartConfig } from "~/hooks";

type ScalesDynamics = RouterOutputs["scores"]["bySphere"]["scalesDynamics"];

type ScalesDynamicsData = NonNullable<
  RouterOutputs["scores"]["bySphere"]["scalesDynamics"]
>["data"];

interface ScalesDynamicsChartProps {
  config?: ChartConfig;
  data?: ScalesDynamicsData;
}

export const ScalesDynmaicsChart: React.FC<ScalesDynamicsChartProps> = ({
  data,
  config,
}) => {
  const { t } = useTranslation("results");
  const { toggleItem, selected, filterData } =
    LinearChartFilter.useLinearChartFilter();

  const toReadableDate = useCallback(
    (date: string | undefined) => dateToReadable(date, true),
    [],
  );

  const toReadableDDMM = useCallback(
    (date: string | undefined) => dateToDDMM(date),
    [],
  );

  if (!config) return null;
  if (!data || data.length === 0) return null;

  return (
    <Card
      data-id={"scales-dynamics-chart-card"}
      paddingVertical={"$4.1"}
      shadow="md"
      gap={"$4.1"}
    >
      <Collapsible.Provider>
        <XStack
          data-id={"scales-dynamics-chart-header"}
          justifyContent="space-between"
          alignItems="center"
        >
          <Card.Title data-id={"scales-dynamics-chart-title"} textAlign="left">
            {t("scalesDynamics.title")}
          </Card.Title>
          <Collapsible.Toggle
            data-id={"scales-dynamics-chart-filter-trigger"}
            iconAfter={<Gear />}
            variant="gray"
            size="sm"
            rounded="lg"
          >
            {t("scalesDynamics.filterTrigger")}
          </Collapsible.Toggle>
        </XStack>

        <Collapsible.Area>
          <Chips.Container data-id={"scales-dynamics-chart-filter"}>
            <View gap={"$0.5"}>
              <Chips.Description
                data-id={"scales-dynamics-chart-filter-description"}
              >
                {t("scalesDynamics.filterDescription")}
              </Chips.Description>
            </View>
            <View
              data-id={"scales-dynamics-chart-filter-chips"}
              gap={"$1"}
              flexDirection="row"
              flexWrap="wrap"
            >
              {Object.entries(config).map(([key, item]) => (
                <Chips.Chip
                  data-id={`scales-dynamics-chart-filter-chip-${key}`}
                  cursor="pointer"
                  key={key}
                  selected={selected(key)}
                  backgroundColor={item.color}
                  onPress={() => toggleItem(key)}
                >
                  {item.label}
                </Chips.Chip>
              ))}
            </View>
          </Chips.Container>
        </Collapsible.Area>
      </Collapsible.Provider>

      <View position="relative">
        <ChartContainer
          data-id={"scales-dynamics-chart-container"}
          height={362}
          config={config}
        >
          <LineChart
            data-id={"scales-dynamics-chart"}
            accessibilityLayer
            data={filterData(data, "date")}
            margin={{
              left: 0,
              right: 40,
              top: 12,
              bottom: 20,
            }}
          >
            <CartesianGrid
              data-id={"scales-dynamics-chart-grid"}
              strokeDasharray={"3 3"}
              stroke="#CBCBCC"
              vertical={false}
            />
            <XAxis
              data-id={"scales-dynamics-chart-x-axis"}
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={24}
              tick={{
                fill: "#636668",
                fontFamily: "Fixel Text",
                fontSize: 14,
                fontWeight: 400,
              }}
              tickFormatter={(v: string | number) => {
                return toReadableDDMM(v.toString()) ?? "";
              }}
            />
            <YAxis
              data-id={"scales-dynamics-chart-y-axis"}
              tickLine={false}
              axisLine={false}
              tickMargin={16}
              tick={(props: TickProps) => <YAxisTick {...props} />}
            />
            <Tooltip
              data-id={"scales-dynamics-chart-tooltip"}
              cursor={{
                stroke: "#CBCBCC",
                strokeWidth: 1,
                strokeDasharray: "3 3",
              }}
              content={
                <ChartTooltip.Content
                  data-id={"scales-dynamics-chart-tooltip-content"}
                >
                  {(payload, _, date) => (
                    <ChartTooltip.Container
                      data-id={"scales-dynamics-chart-tooltip-container"}
                    >
                      <ChartTooltip.Title
                        data-id={"scales-dynamics-chart-tooltip-title"}
                      >
                        {toReadableDate(date)}
                      </ChartTooltip.Title>
                      <View
                        gap={"$0.5"}
                        data-id={"scales-dynamics-chart-tooltip-body"}
                      >
                        {payload?.map(({ color, value, config }, i) => (
                          <XStack
                            data-id={"scales-dynamics-chart-tooltip-item"}
                            gap={"$0.5"}
                            alignItems="center"
                            key={i}
                          >
                            <Circle
                              backgroundColor={"white"}
                              borderWidth={"$0.3"}
                              borderColor={color}
                              size={"$2"}
                            />

                            <Text
                              data-id={"scales-dynamics-chart-tooltip-text"}
                              fontFamily={"$body"}
                              fontSize={"$1"}
                              color={"black"}
                            >
                              {config?.label} {value}
                            </Text>
                          </XStack>
                        ))}
                      </View>
                    </ChartTooltip.Container>
                  )}
                </ChartTooltip.Content>
              }
            />
            {getChartLines(config).map(({ dataKey, color }, i) => (
              <Line
                data-id={`scales-dynamics-chart-line-${dataKey}`}
                key={i}
                dataKey={dataKey}
                type="linear"
                stroke={color}
                strokeWidth={2.5}
                dot={{
                  r: 8,
                  strokeWidth: 2.5,
                  stroke: color,
                  fill: "white",
                }}
                activeDot={{
                  r: 3,
                  strokeWidth: 2.5,
                  stroke: color,
                  fill: color,
                }}
              />
            ))}
          </LineChart>
        </ChartContainer>

        <Legend config={filterData([config])[0] as ChartConfig} />
      </View>
    </Card>
  );
};

export const ScalesDynmaicsChartWithFilter: React.FC<{
  data: ScalesDynamics;
}> = ({ data }) => {
  const config = useGetChartConfig(data);
  if (data === undefined) return null;
  return (
    <LinearChartFilter.Provider defaultSelected={data?.keys ?? []}>
      <ScalesDynmaicsChart data={data?.data} config={config} />
    </LinearChartFilter.Provider>
  );
};

export const ScalesDynmaicsChartMobile: React.FC<ScalesDynamicsChartProps> = ({
  data,
  config,
}) => {
  const { t } = useTranslation("results");
  const { toggleItem, selected, filterData } =
    LinearChartFilter.useLinearChartFilter();

  const toReadableDate = useCallback(
    (date: string | undefined) => dateToReadable(date, true),
    [],
  );

  const toReadableDDMM = useCallback(
    (date: string | undefined) => dateToDDMM(date),
    [],
  );

  if (!config) return null;
  if (!data || data.length === 0) return null;

  return (
    <View
      backgroundColor={"$warm-snow-500"}
      paddingVertical={"$4"}
      paddingHorizontal={"$1.2"}
      gap={"$3"}
    >
      <Collapsible.Provider>
        <YStack justifyContent="center" gap={"$2"} alignItems="center">
          <Text
            fontFamily={"$subtitle"}
            fontWeight={"$6"}
            fontSize={"$5"}
            lineHeight={"$5"}
            textAlign={"center"}
            color={"$gray-primary-900"}
          >
            {t("scalesDynamics.title")}
          </Text>

          <Collapsible.Toggle
            iconAfter={<Gear />}
            variant="gray"
            size="sm"
            rounded="lg"
          >
            {t("scalesDynamics.filterTrigger")}
          </Collapsible.Toggle>
        </YStack>

        <Collapsible.Area>
          <Chips.Container backgroundColor={"white"}>
            <Chips.Description
              data-id={"scales-dynamics-chart-filter-description"}
            >
              {t("scalesDynamics.filterDescription")}
            </Chips.Description>
            <View
              gap={"$1"}
              flexDirection="row"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="center"
            >
              {Object.entries(config).map(([key, item]) => (
                <Chips.Chip
                  cursor="pointer"
                  key={key}
                  selected={selected(key)}
                  backgroundColor={item.color}
                  onPress={() => toggleItem(key)}
                >
                  {item.label}
                </Chips.Chip>
              ))}
            </View>
          </Chips.Container>
        </Collapsible.Area>
      </Collapsible.Provider>

      <View gap={"$3"} position="relative">
        <ChartContainer height={362} config={config}>
          <LineChart
            accessibilityLayer
            data={filterData(data, "date")}
            margin={{
              left: 0,
              right: 40,
              top: 12,
              bottom: 24,
            }}
          >
            <CartesianGrid
              strokeDasharray={"3 3"}
              stroke="#CBCBCC"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={12}
              tick={{
                fill: "#636668",
                fontFamily: "Fixel Text",
                fontSize: 14,
                fontWeight: 400,
              }}
              tickFormatter={(v: string | number) => {
                return toReadableDDMM(v.toString()) ?? "";
              }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={16}
              tick={(props: TickProps) => <YAxisTick {...props} />}
            />
            <Tooltip
              cursor={{
                stroke: "#CBCBCC",
                strokeWidth: 1,
                strokeDasharray: "3 3",
              }}
              content={
                <ChartTooltip.Content>
                  {(payload, _, date) => (
                    <ChartTooltip.Container>
                      <ChartTooltip.Title>
                        {toReadableDate(date)}
                      </ChartTooltip.Title>
                      <View gap={"$0.5"}>
                        {payload?.map(({ color, value, config }, i) => (
                          <XStack gap={"$0.5"} alignItems="center" key={i}>
                            <Circle
                              backgroundColor={"white"}
                              borderWidth={"$0.3"}
                              borderColor={color}
                              size={"$2"}
                            />

                            <Text
                              fontFamily={"$body"}
                              fontSize={"$1"}
                              color={"black"}
                            >
                              {config?.label} {value}
                            </Text>
                          </XStack>
                        ))}
                      </View>
                    </ChartTooltip.Container>
                  )}
                </ChartTooltip.Content>
              }
            />
            {getChartLines(config).map(({ dataKey, color }, i) => (
              <Line
                key={i}
                dataKey={dataKey}
                type="linear"
                stroke={color}
                strokeWidth={2.5}
                dot={{
                  r: 8,
                  strokeWidth: 2.5,
                  stroke: color,
                  fill: "white",
                }}
                activeDot={{
                  r: 3,
                  strokeWidth: 2.5,
                  stroke: color,
                  fill: color,
                }}
              />
            ))}
          </LineChart>
        </ChartContainer>

        <Legend
          backgroundColor={"white"}
          config={filterData([config])[0] as ChartConfig}
        />
      </View>
    </View>
  );
};

export const ScalesDynmaicsChartMobileWithFilter: React.FC<{
  data: ScalesDynamics;
}> = ({ data }) => {
  const config = useGetChartConfig(data);

  if (data === undefined) return null;
  return (
    <LinearChartFilter.Provider defaultSelected={data?.keys ?? []}>
      <ScalesDynmaicsChartMobile config={config} data={data?.data} />
    </LinearChartFilter.Provider>
  );
};
